import {createReducer, on} from '@ngrx/store';
import {CatalogSummary, CollectionMetadata} from '../../../models/clare';
import {RegistrationSummary} from '../../../models';
import {ProductManagementErrorInterface} from '../../../error/error-handling/product-management-error';
import {CatalogActions} from './catalog.actions';

export const CATALOG_STATE_KEY = 'catalog';

export type CreateCatalogStatus = 'idle' | 'loading' | 'success' | 'failure';
export type DeleteCatalogStatus = CreateCatalogStatus;

export interface DeleteCatalogRequestState {
  status: DeleteCatalogStatus;
  catalogId: number | undefined;
}

export type CatalogRegistrationsStatus = 'idle' | 'loading' | 'success' | 'failure';

export interface CatalogRegistrationsRequestState {
  status: CatalogRegistrationsStatus;
  catalogId: number | undefined;
  registrations: RegistrationSummary[];
  error: ProductManagementErrorInterface | undefined;
}

export interface CatalogState {
  catalogs: CatalogSummary[];
  paginationMeta: CollectionMetadata;
  createdCatalogId: number | undefined;
  createStatus: CreateCatalogStatus;
  deletedCatalogId: number | undefined;
  deleteStatus: DeleteCatalogStatus;
  catalogRegistrations: CatalogRegistrationsRequestState;
  loading: boolean;
  loaded: boolean;
  error: ProductManagementErrorInterface | undefined;
}

export const initialCatalogState: CatalogState = {
  catalogs: [],
  paginationMeta: {result_type: 'Catalog', pagination: {}} as CollectionMetadata,
  createdCatalogId: undefined,
  createStatus: 'idle',
  deletedCatalogId: undefined,
  deleteStatus: 'idle',
  catalogRegistrations: {
    status: 'idle',
    catalogId: undefined,
    registrations: [],
    error: undefined,
  },
  loading: false,
  loaded: false,
  error: undefined,
};

export const catalogReducer = createReducer(
  initialCatalogState,
  on(CatalogActions.loadCatalogs, (state): CatalogState => ({
    ...state,
    catalogs: [],
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(CatalogActions.loadCatalogsSuccess, (state, {catalogs}): CatalogState => ({
    ...state,
    catalogs: [...catalogs.result],
    paginationMeta: catalogs.metadata,
    loading: false,
    loaded: true,
    error: undefined,
  })),
  on(CatalogActions.createCatalog, (state): CatalogState => ({
    ...state,
    createStatus: 'loading',
    createdCatalogId: undefined,
    error: undefined,
  })),
  on(CatalogActions.createCatalogSuccess, (state, {catalog}): CatalogState => ({
    ...state,
    createdCatalogId: catalog.id,
    createStatus: 'success',
    error: undefined,
  })),
  on(CatalogActions.createCatalogFailure, (state, {error}): CatalogState => ({
    ...state,
    createStatus: 'failure',
    createdCatalogId: undefined,
    error,
  })),
  on(CatalogActions.resetCreateCatalogStatus, (state): CatalogState => ({
    ...state,
    createStatus: 'idle',
    createdCatalogId: undefined,
    error: undefined,
  })),
  on(CatalogActions.loadCatalogsFailure, (state, {error}): CatalogState => ({
    ...state,
    catalogs: [],
    loading: false,
    loaded: false,
    error,
  })),
  on(CatalogActions.deleteCatalog, (state, {catalogId}): CatalogState => ({
    ...state,
    deletedCatalogId: catalogId,
    deleteStatus: 'loading',
    error: undefined,
  })),
  on(CatalogActions.deleteCatalogSuccess, (state, {catalogId}): CatalogState => ({
    ...state,
    deletedCatalogId: catalogId,
    deleteStatus: 'success',
    error: undefined,
  })),
  on(CatalogActions.deleteCatalogFailure, (state, {catalogId, error}): CatalogState => ({
    ...state,
    deletedCatalogId: catalogId,
    deleteStatus: 'failure',
    error,
  })),
  on(CatalogActions.resetDeleteCatalogStatus, (state): CatalogState => ({
    ...state,
    deletedCatalogId: undefined,
    deleteStatus: 'idle',
    error: undefined,
  })),
  on(CatalogActions.loadCatalogRegistrations, (state, {catalogId}): CatalogState => ({
    ...state,
    catalogRegistrations: {
      status: 'loading',
      catalogId,
      registrations: [],
      error: undefined,
    },
  })),
  on(CatalogActions.loadCatalogRegistrationsSuccess, (state, {catalogId, registrations}): CatalogState => ({
    ...state,
    catalogRegistrations: {
      status: 'success',
      catalogId,
      registrations,
      error: undefined,
    },
  })),
  on(CatalogActions.loadCatalogRegistrationsFailure, (state, {catalogId, error}): CatalogState => ({
    ...state,
    catalogRegistrations: {
      status: 'failure',
      catalogId,
      registrations: [],
      error,
    },
  })),
  on(CatalogActions.resetCatalogRegistrations, (state): CatalogState => ({
    ...state,
    catalogRegistrations: initialCatalogState.catalogRegistrations,
  }))
);

export const getCatalogs = (state: CatalogState) => state.catalogs;
export const getPaginationMeta = (state: CatalogState): CollectionMetadata => state.paginationMeta;
export const getCreatedCatalogId = (state: CatalogState) => state.createdCatalogId;
export const getCreateStatus = (state: CatalogState) => state.createStatus;
export const getDeletedCatalogId = (state: CatalogState) => state.deletedCatalogId;
export const getDeleteStatus = (state: CatalogState) => state.deleteStatus;
export const getCatalogRegistrations = (state: CatalogState) => state.catalogRegistrations;
export const getLoading = (state: CatalogState) => state.loading;
export const getLoaded = (state: CatalogState) => state.loaded;
export const getError = (state: CatalogState) => state.error;
